import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { catalogApi, type ProductInput } from '@/api/catalogApi'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ListState, StatusPill, SuperAdminGate, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { ProductForm } from '@/components/admin/catalog/ProductForm'
import { useCatalogMutation, useCatalogProducts } from '@/hooks/useCatalog'

/** /admin/products — the product catalog (superadmin). */
export default function CatalogProductsPage() {
  return (
    <SuperAdminGate>
      <ProductsList />
    </SuperAdminGate>
  )
}

function ProductsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const products = useCatalogProducts()
  const [creating, setCreating] = useState(false)
  const create = useCatalogMutation((input: ProductInput) => catalogApi.createProduct(input))

  return (
    <Page>
      <PageHeading
        eyebrow={t('catalog.eyebrow')}
        title={t('catalog.products.title')}
        actions={<Button onClick={() => setCreating(true)}>{t('catalog.products.new')}</Button>}
      />
      <ListState query={products}>
        {(products.data ?? []).length === 0 ? (
          <p className="py-6 text-meta text-ink-muted">{t('catalog.products.empty')}</p>
        ) : (
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-surface-subtle">
              <tr>
                <th className={thClass}>{t('catalog.products.cols.name')}</th>
                <th className={`${thClass} w-44`}>{t('catalog.products.cols.key')}</th>
                <th className={`${thClass} w-32`}>{t('catalog.products.cols.components')}</th>
                <th className={`${thClass} w-32`}>{t('catalog.products.cols.sla')}</th>
                <th className={`${thClass} w-28`}>{t('catalog.products.cols.tickets')}</th>
                <th className={`${thClass} w-28`}>{t('catalog.products.cols.status')}</th>
              </tr>
            </thead>
            <tbody>
              {(products.data ?? []).map((p) => (
                <tr key={p.id} className="border-b border-line hover:bg-surface-subtle">
                  <td className={`${tdClass} font-medium text-ink-strong`}>
                    <Link to={`/admin/products/${p.id}`} className="hover:underline">
                      {p.name}
                    </Link>
                    {p.description ? <p className="truncate text-label font-normal text-ink-muted">{p.description}</p> : null}
                  </td>
                  <td className={`${tdClass} font-mono`}>{p.key}</td>
                  <td className={`${tdClass} font-mono`}>{p._count.components}</td>
                  <td className={`${tdClass} font-mono`}>{p.slaTargetPct}%</td>
                  <td className={`${tdClass} font-mono`}>{p._count.tickets}</td>
                  <td className={tdClass}>
                    <StatusPill status={p.active ? 'active' : 'inactive'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListState>

      <Dialog
        open={creating}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false)
            create.reset()
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('catalog.products.createTitle')}</DialogTitle>
          </DialogHeader>
          {creating ? (
            <ProductForm
              pending={create.isPending}
              error={create.error}
              onCancel={() => setCreating(false)}
              onSubmit={(input) =>
                create.mutate(input, { onSuccess: (p) => navigate(`/admin/products/${p.id}?tab=components`) })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Page>
  )
}
